import { useEffect, useState, useCallback, useRef } from "react";
import api, { errMsg } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ChevronRight, Layers, Upload, Download, Sparkles } from "lucide-react";

export default function Classifications() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [families, setFamilies] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [selFam, setSelFam] = useState(null);
  const [selGrp, setSelGrp] = useState(null);
  const [open, setOpen] = useState(false);
  const [formLevel, setFormLevel] = useState("family");
  const [form, setForm] = useState({ code: "", name: "", description: "" });

  const loadFamilies = useCallback(() => {
    api.get("/classifications", { params: { level: "family" } }).then((r) => setFamilies(r.data));
  }, []);
  useEffect(() => { loadFamilies(); }, [loadFamilies]);
  useEffect(() => {
    if (selFam) api.get("/classifications", { params: { level: "group", parent_id: selFam.id } }).then((r) => setGroups(r.data));
    else setGroups([]);
    setSelGrp(null);
  }, [selFam]);
  useEffect(() => {
    if (selGrp) api.get("/classifications", { params: { level: "subgroup", parent_id: selGrp.id } }).then((r) => setSubgroups(r.data));
    else setSubgroups([]);
  }, [selGrp]);

  const openNew = (level) => {
    setFormLevel(level); setForm({ code: "", name: "", description: "" }); setOpen(true);
  };

  const save = async () => {
    try {
      const parent_id = formLevel === "family" ? "" : formLevel === "group" ? selFam?.id : selGrp?.id;
      await api.post("/classifications", {
        level: formLevel, code: form.code.toUpperCase(), name: form.name,
        parent_id, description: form.description,
      });
      toast.success(t("common.saved"));
      setOpen(false);
      if (formLevel === "family") loadFamilies();
      else if (formLevel === "group") api.get("/classifications", { params: { level: "group", parent_id: selFam.id } }).then((r) => setGroups(r.data));
      else api.get("/classifications", { params: { level: "subgroup", parent_id: selGrp.id } }).then((r) => setSubgroups(r.data));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (cid, refresh) => {
    if (!window.confirm(t("cls.confirmDelete"))) return;
    try { await api.delete(`/classifications/${cid}`); toast.success(t("common.deleted")); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const editable = can("admin", "buyer");
  const isAdmin = can("admin");
  const fileRef = useRef(null);

  const reloadAll = () => {
    loadFamilies();
    if (selFam) api.get("/classifications", { params: { level: "group", parent_id: selFam.id } }).then((r) => setGroups(r.data));
    if (selGrp) api.get("/classifications", { params: { level: "subgroup", parent_id: selGrp.id } }).then((r) => setSubgroups(r.data));
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/classifications-template", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "plantilla_clasificaciones.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const importCsv = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/classifications-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("cls.importDone").replace("{created}", r.data.created).replace("{skipped}", r.data.skipped));
      if (r.data.errors?.length) toast.error(r.data.errors.slice(0, 3).join(" · "));
      reloadAll();
    } catch (e) { toast.error(errMsg(e)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const seedMining = async () => {
    if (!window.confirm(t("cls.seedConfirm"))) return;
    try {
      const r = await api.post("/masters-seed-mining");
      const c = r.data.classifications, e = r.data.equipment;
      toast.success(t("cls.seedDone")
        .replace("{c1}", c.created).replace("{s1}", c.skipped)
        .replace("{c2}", e.created).replace("{s2}", e.skipped));
      reloadAll();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const Column = ({ title, items, selected, onSelect, onDelete, onAdd, addLabel, disabled, testid }) => (
    <div className="border bg-card flex-1 min-w-0" data-testid={testid}>
      <div className="border-b p-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Layers size={12} className="text-primary" />
          <span className="text-[10px] font-bold uppercase tracking-widest">{title}</span>
        </div>
        {editable && !disabled && (
          <button onClick={onAdd} className="text-primary hover:text-primary/70" data-testid={`${testid}-add`}>
            <Plus size={13} />
          </button>
        )}
      </div>
      {disabled ? (
        <div className="p-6 text-center text-[11px] text-muted-foreground">{addLabel}</div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center text-[11px] text-muted-foreground">—</div>
      ) : (
        <ul className="divide-y">
          {items.map((it) => (
            <li key={it.id}
                onClick={() => onSelect(it)}
                data-testid={`${testid}-item-${it.code}`}
                className={`px-3 py-2 flex items-center gap-2 cursor-pointer text-xs group hover:bg-muted transition-colors ${
                  selected?.id === it.id ? "bg-primary/5 border-l-2 border-primary" : ""
                }`}>
              <span className="font-black tabular-nums w-12 shrink-0 text-primary">{it.code}</span>
              <span className="flex-1 truncate">{it.name}</span>
              {onDelete && editable && (
                <button onClick={(e) => { e.stopPropagation(); onDelete(it.id); }}
                  className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`${testid}-delete-${it.code}`}>
                  <Trash2 size={11} />
                </button>
              )}
              {onSelect && <ChevronRight size={11} className="text-muted-foreground shrink-0" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div data-testid="classifications-page">
      <PageHeader title={t("cls.title")} subtitle={t("cls.subtitle")}>
        {editable && (
          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept=".csv" className="hidden"
              onChange={(e) => importCsv(e.target.files?.[0])} data-testid="cls-import-input" />
            <Button size="sm" variant="outline" onClick={downloadTemplate}
              className="rounded-none text-xs font-bold h-8" data-testid="cls-template-button">
              <Download size={13} className="mr-1" /> {t("cls.template")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}
              className="rounded-none text-xs font-bold h-8" data-testid="cls-import-button">
              <Upload size={13} className="mr-1" /> {t("cls.import")}
            </Button>
            {isAdmin && (
              <Button size="sm" onClick={seedMining}
                className="rounded-none text-xs font-bold h-8" data-testid="cls-seed-button">
                <Sparkles size={13} className="mr-1" /> {t("cls.seed")}
              </Button>
            )}
          </div>
        )}
      </PageHeader>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Column title={t("cls.family")} items={families} selected={selFam} onSelect={setSelFam}
                onDelete={(id) => del(id, loadFamilies)} onAdd={() => openNew("family")}
                addLabel={t("cls.selectAbove")} testid="col-family" />
        <Column title={t("cls.group")} items={groups} selected={selGrp} onSelect={setSelGrp}
                onDelete={selFam ? (id) => del(id, () => api.get("/classifications", { params: { level: "group", parent_id: selFam.id } }).then((r) => setGroups(r.data))) : null}
                onAdd={() => openNew("group")} addLabel={t("cls.selectFamily")}
                disabled={!selFam} testid="col-group" />
        <Column title={t("cls.subgroup")} items={subgroups} selected={null} onSelect={null}
                onDelete={selGrp ? (id) => del(id, () => api.get("/classifications", { params: { level: "subgroup", parent_id: selGrp.id } }).then((r) => setSubgroups(r.data))) : null}
                onAdd={() => openNew("subgroup")} addLabel={t("cls.selectGroup")}
                disabled={!selGrp} testid="col-subgroup" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="uppercase font-black">{t(`cls.new.${formLevel}`)}</DialogTitle>
            <DialogDescription className="text-xs">
              {formLevel === "family" && t("cls.familyExample")}
              {formLevel === "group" && `${t("cls.family")}: ${selFam?.name}`}
              {formLevel === "subgroup" && `${t("cls.family")}: ${selFam?.name} → ${t("cls.group")}: ${selGrp?.name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("cls.code")}</Label>
              <Input value={form.code} maxLength={4} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="rounded-none h-8 text-xs font-black tabular-nums" data-testid="cls-form-code" placeholder="MEC / BOM / CEN" />
              <div className="text-[10px] text-muted-foreground">{t("cls.codeHelp")}</div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("cls.name")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none h-8 text-xs" data-testid="cls-form-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.description")}</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="rounded-none h-8 text-xs" data-testid="cls-form-description" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={!form.code || !form.name}
              className="rounded-none font-bold text-xs" data-testid="cls-save-button">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
